/**
 * PdfCanvasViewer — Jun-2026 (Bugfix „PDFs lassen sich nicht öffnen").
 *
 * Rendert ein PDF-Blob über PDF.js (pdfjs-dist) als Canvas-Seiten. Damit ist die
 * Anzeige UNABHÄNGIG vom nativen PDF-Viewer des Browsers — also auch dann
 * zuverlässig, wenn das frühere `blob:`-iframe leer blieb (z. B. Browser-
 * Einstellung „PDFs herunterladen statt öffnen" oder fehlendes PDF-Plugin).
 * Bei einem Render-Fehler greifen die Fallbacks „Neuer Tab" / „Herunterladen".
 */
import { useEffect, useRef, useState } from "react";
import * as pdfjsLib from "pdfjs-dist";

// Jul-2026 (Bugfix „PDF-Vorschau nur grau") — Worker als STATISCHE Datei aus
// /public ausliefern (same-origin, unabhängig vom Bundler/CDN). Die frühere
// `new URL(..., import.meta.url)`-Variante schlug im Produktions-Build fehl,
// wodurch die Vorschau dauerhaft grau blieb.
pdfjsLib.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL || ""}/pdf.worker.min.js`;

export default function PdfCanvasViewer({ blob }) {
    const containerRef = useRef(null);
    const [status, setStatus] = useState("loading"); // loading | ok | error

    useEffect(() => {
        if (!blob) return undefined;
        let cancelled = false;
        let task = null;
        const container = containerRef.current;

        (async () => {
            try {
                setStatus("loading");
                const buf = await blob.arrayBuffer();
                if (cancelled) return;
                task = pdfjsLib.getDocument({ data: buf });
                const pdf = await task.promise;
                if (cancelled || !container) return;
                container.innerHTML = "";
                const dpr = window.devicePixelRatio || 1;
                const targetWidth = (container.clientWidth || 900) - 24;
                setStatus("ok"); // erste Seite folgt sofort — kein Dauer-Grau mehr
                for (let i = 1; i <= pdf.numPages; i++) {
                    const pageObj = await pdf.getPage(i);
                    if (cancelled) return;
                    const base = pageObj.getViewport({ scale: 1 });
                    const scale = Math.min(2.2, Math.max(0.5, targetWidth / base.width));
                    const viewport = pageObj.getViewport({ scale: scale * dpr });
                    const canvas = document.createElement("canvas");
                    canvas.width = Math.floor(viewport.width);
                    canvas.height = Math.floor(viewport.height);
                    canvas.style.width = `${Math.floor(viewport.width / dpr)}px`;
                    canvas.style.maxWidth = "100%";
                    canvas.className = "mx-auto mb-3 shadow-md bg-white block";
                    canvas.setAttribute("data-testid", `pdf-canvas-page-${i}`);
                    container.appendChild(canvas);
                    await pageObj.render({ canvasContext: canvas.getContext("2d"), viewport }).promise;
                }
                if (!cancelled) setStatus("ok");
            } catch (e) {
                if (!cancelled) setStatus("error");
            }
        })();

        // Jul-2026 (Bugfix „nur grau") — Watchdog: hängt der Worker (z. B. Datei
        // nicht ladbar), nach 15 s klaren Fehler-Fallback statt Endlos-Grau zeigen.
        const watchdog = setTimeout(() => {
            if (!cancelled) setStatus((s) => (s === "loading" ? "error" : s));
        }, 15000);

        return () => {
            cancelled = true;
            clearTimeout(watchdog);
            try { if (task && task.destroy) task.destroy(); } catch { /* noop */ }
        };
    }, [blob]);

    // Jul-2026 (User-Report „Dokumente lassen sich nicht mit der Maus scrollen"):
    // Radix-Dialoge im Hintergrund sperren das Rad-Scrollen (react-remove-scroll).
    // Wir scrollen den Viewer deshalb selbst per Wheel-Event.
    const scrollRef = useRef(null);
    useEffect(() => {
        const el = scrollRef.current;
        if (!el) return undefined;
        const onWheel = (e) => {
            el.scrollTop += e.deltaY;
            el.scrollLeft += e.deltaX;
            e.preventDefault();
            e.stopPropagation();
        };
        el.addEventListener("wheel", onWheel, { passive: false });
        return () => el.removeEventListener("wheel", onWheel);
    }, []);

    return (
        <div ref={scrollRef} className="flex-1 overflow-auto bg-gray-300/60" data-testid="pdf-canvas-viewer">
            {status === "loading" && (
                <div className="p-10 text-center text-gray-500 text-sm" data-testid="pdf-canvas-loading">PDF wird geladen…</div>
            )}
            {status === "error" && (
                <div className="p-10 text-center text-gray-600 text-sm" data-testid="pdf-canvas-error">
                    Die Vorschau konnte nicht gerendert werden. Bitte „Neuer Tab" oder „Herunterladen" nutzen.
                </div>
            )}
            <div ref={containerRef} className="py-4 px-2" />
        </div>
    );
}
